import { useState } from 'react';
import { ShoppingBag, Minus, Plus, Trash2, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import { useFarmCart } from '@/context/FarmCartContext';
import { formatPrice } from '@/lib/formatMoney';
import { FarmlyCheckoutDeliveryStep } from './FarmlyCheckoutDeliveryStep';

/**
 * Global cart sheet that uses Farmly native checkout with Stripe.
 * Shopify is disabled - all checkout goes through Farmly.
 */
export function GlobalCartSheet() {
  const { cart, totalPrice, isCartOpen, closeCart, updateQuantity, removeFromCart } = useFarmCart();
  const [isDeliveryOpen, setIsDeliveryOpen] = useState(false);

  const handleProceedToDelivery = () => {
    // Close cart sheet and open delivery step
    closeCart();
    setIsDeliveryOpen(true);
  };

  const handleDeliveryClose = () => {
    setIsDeliveryOpen(false);
  };

  const handleDeliveryBack = () => {
    // Go back to cart - don't auto-reopen, user can click cart icon
    setIsDeliveryOpen(false);
  };

  return (
    <>
      <Sheet open={isCartOpen} onOpenChange={(open) => !open && closeCart()}>
        <SheetContent className="flex flex-col w-full sm:max-w-md">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <ShoppingBag className="h-5 w-5" />
              Din varukorg
            </SheetTitle>
            {cart && (
              <SheetDescription>
                Du handlar från {cart.producerName}
              </SheetDescription>
            )}
          </SheetHeader>

          {!cart || cart.items.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center py-12">
              <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center mb-4">
                <ShoppingBag className="h-8 w-8 text-muted-foreground" />
              </div>
              <p className="text-muted-foreground">
                Din varukorg är tom
              </p>
            </div>
          ) : (
            <>
              {/* Cart Items */}
              <div className="flex-1 overflow-y-auto py-4 -mx-6 px-6">
                <div className="space-y-4">
                  {cart.items.map((item) => (
                    <div key={item.product.id} className="flex gap-3">
                      {/* Image */}
                      <div className="h-16 w-16 rounded-lg bg-muted overflow-hidden shrink-0">
                        {item.product.imageUrl ? (
                          <img
                            src={item.product.imageUrl}
                            alt={item.product.imageAlt || item.product.title}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <div className="h-full w-full flex items-center justify-center">
                            <ShoppingBag className="h-6 w-6 text-muted-foreground" />
                          </div>
                        )}
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-foreground text-sm line-clamp-1">
                          {item.product.title}
                        </h4>
                        <p className="text-sm text-muted-foreground">
                          {formatPrice(item.product.price, item.product.currencyCode)}
                        </p>

                        {/* Quantity controls */}
                        <div className="flex items-center gap-2 mt-2">
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => updateQuantity(item.product.id, item.quantity - 1)}
                          >
                            <Minus className="h-3 w-3" />
                          </Button>
                          <span className="text-sm font-medium w-6 text-center">
                            {item.quantity}
                          </span>
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => updateQuantity(item.product.id, item.quantity + 1)}
                          >
                            <Plus className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 ml-auto text-muted-foreground hover:text-destructive"
                            onClick={() => removeFromCart(item.product.id)}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <Separator />

              {/* Footer */}
              <SheetFooter className="flex-col gap-4 pt-4 sm:flex-col">
                <div className="flex items-center justify-between w-full">
                  <span className="font-medium text-foreground">Summa</span>
                  <span className="font-semibold text-lg text-foreground">
                    {formatPrice(totalPrice, cart.items[0]?.product.currencyCode || 'SEK')}
                  </span>
                </div>

                <Button
                  className="w-full gap-2"
                  size="lg"
                  onClick={handleProceedToDelivery}
                >
                  Gå till kassan
                  <ExternalLink className="h-4 w-4" />
                </Button>

                <p className="text-xs text-muted-foreground text-center">
                  Välj leveransalternativ i nästa steg
                </p>
              </SheetFooter>
            </>
          )}
            <div className="bottom-sheet-spacer" />
        </SheetContent>
      </Sheet>

      {/* Delivery Step Sheet - Farmly Native Checkout */}
      <FarmlyCheckoutDeliveryStep
        open={isDeliveryOpen}
        onClose={handleDeliveryClose}
        onBack={handleDeliveryBack}
      />
    </>
  );
}
